import apiClient from './apiService';

// ============================================================================
// AI assistant — typed calls to /api/v1/ai/* (backend/routes/ai_routes.py).
// Every call goes through the shared apiClient so the Bearer token is injected
// by its interceptor. A chat turn runs one or more model calls plus native API
// calls, so it gets a longer timeout than ordinary CRUD.
// ============================================================================

const CHAT_TIMEOUT_MS = 180_000;

export interface AiStep {
  tool: string;
  op?: string;
  noun?: string;
  outcome: string;
  summary?: string;
  api?: string;
}

export interface AiPlanStep {
  n: number;
  ref?: string;
  summary: string;
  api: string;
  kind?: 'read' | 'write';
}

export type AiDecision = 'confirm' | 'cancel' | 'retry' | 'rollback' | 'keep';

/** A card waiting on the person: one change, a whole plan, or a plan that stopped part-way. */
export interface AiPending {
  summary: string;
  api?: string;
  kind?: 'plan' | 'plan_failed';
  title?: string;
  steps?: AiPlanStep[];
  error?: string;
  done?: number[];
  failed?: number;
  actions?: AiDecision[];
}

export interface AiTurn {
  conversation_id: string;
  reply: string;
  steps: AiStep[];
  pending: AiPending | null;
  at?: string;
  acting_as?: { name?: string; entity?: string };
  ai_calls?: number;
}

export type AiItem =
  | { role: 'user'; text: string; at: string }
  | { role: 'assistant'; text: string; steps: AiStep[]; at: string }
  | ({ role: 'pending'; at: string; decided?: string } & AiPending);

export interface AiConversation {
  id: string;
  title: string;
  at: string | null;
  waiting: boolean;
}

export interface AiProvider {
  value: 'anthropic' | 'bedrock' | 'openai';
  label: string;
  available: boolean;
  secrets: string[];
  needs_region: boolean;
  model_hint?: string | null;
  models: { value: string; label: string }[];
}

export interface AiSettings {
  is_enabled: boolean;
  provider: AiProvider['value'];
  model: string;
  region: string | null;
  has_key: boolean;
  key_hint: string | null;
  modified_on: string | null;
  providers: AiProvider[];
  models: { value: string; label: string }[];
}

export interface AiSettingsUpdate {
  provider?: AiProvider['value'];
  is_enabled?: boolean;
  model?: string;
  region?: string;
  api_key?: string;
  aws_access_key_id?: string;
  aws_secret_access_key?: string;
}

export const sendChat = async (body: {
  conversation_id: string | null;
  message: string;
  screen?: string;
  persona?: string;
}): Promise<AiTurn> => {
  const r = await apiClient.post<AiTurn>('/api/v1/ai/chat', body, { timeout: CHAT_TIMEOUT_MS });
  return r.data;
};

export const decideChat = async (id: string, decision: AiDecision): Promise<AiTurn> => {
  const r = await apiClient.post<AiTurn>(`/api/v1/ai/chat/${id}/decide`, { decision }, { timeout: CHAT_TIMEOUT_MS });
  return r.data;
};

export const listChats = async (): Promise<AiConversation[]> => (await apiClient.get<AiConversation[]>('/api/v1/ai/chat')).data;

export const openChat = async (id: string): Promise<{ conversation_id: string; transcript: AiItem[]; pending: AiPending | null }> =>
  (await apiClient.get(`/api/v1/ai/chat/${id}`)).data;

export const deleteChat = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/v1/ai/chat/${id}`);
};

export const getAiSettings = async (): Promise<AiSettings> => (await apiClient.get<AiSettings>('/api/v1/ai/settings')).data;

export const updateAiSettings = async (data: AiSettingsUpdate): Promise<AiSettings> =>
  (await apiClient.put<AiSettings>('/api/v1/ai/settings', data)).data;

export interface AiModelList {
  provider: string;
  live: boolean;                       // true = fetched from the provider with the organisation's own credentials
  models: { value: string; label: string }[];
  note?: string;
}

export const getAiModels = async (provider?: string): Promise<AiModelList> =>
  (await apiClient.get<AiModelList>('/api/v1/ai/settings/models', { params: provider ? { provider } : {}, timeout: 30_000 })).data;

export const deleteAiKey = async (): Promise<AiSettings> => (await apiClient.delete<AiSettings>('/api/v1/ai/settings/key')).data;

/** The API's own words for a failure (FastAPI `detail`), else a generic line. */
export const aiErrorText = (e: any, fallback = 'The assistant could not be reached.'): string => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg).filter(Boolean).join('; ') || fallback;
  return fallback;
};
